/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { createPortal } from "react-dom";
import { Alert } from "@/subframe/components/Alert";
import moment from "moment";
import SystemUser from "./messages/SystemUser";
import Recipient from "./messages/Recipient";
import { Loader } from "@/subframe/components/Loader";

interface Message {
  message_id?: string;
  id?: string;
  recipient?: string;
  system_user?: string;
  created_at: string;
  status?: "sent" | "delivered" | "read" | "passed";
  type?: string;
  recipient_payload?: {
    entry: [
      {
        changes: [
          {
            value: {
              messages: [
                {
                  type:
                  | "text"
                  | "video"
                  | "document"
                  | "image"
                  | "audio"
                  | "button"
                  | "location";
                  text?: {
                    body: string;
                  };
                  video?: {
                    caption?: string;
                    id?: number;
                  };
                  document?: {
                    caption?: string;
                    id?: number;
                    filename?: string;
                    mime_type?: string;
                  };
                  image?: {
                    caption?: string;
                    id?: number;
                  };
                  audio?: {
                    voice: boolean;
                  };
                }
              ];
            };
          }
        ];
      }
    ];
  };
  system_user_payload?: {
    image?: {
      id: number;
      caption: string;
    };
    document?: {
      id: number;
      caption: string;
      mime_type: any;
    };
    video?: {
      id: number;
      caption: string;
    };
    type: any;
    template: any;
  };
}

interface MessagesDataWithPagination {
  items: Message[];
  total?: number;
  page?: number;
  size?: number;
}

interface MessageProps {
  messages: MessagesDataWithPagination; // Use the existing interface
  scrollRef: React.RefObject<HTMLDivElement>;
  is24HoursWindowShow: boolean;
}

const Message: React.FC<MessageProps> = ({
  messages,
  scrollRef,
  is24HoursWindowShow,
}) => {
  // Group messages by date
  const groupedMessages = messages?.items?.reduce((acc, message) => {
    const messageDate = moment(message.created_at).format("YYYY-MM-DD");
    if (!acc[messageDate]) {
      acc[messageDate] = [];
    }
    acc[messageDate].push(message);
    return acc;
  }, {} as Record<string, typeof messages.items>);

  return (
    <div className="conveythis-no-translate relative flex min-w-0 w-full grow shrink-0 basis-0 flex-col items-start gap-4 overflow-x-hidden overflow-y-auto bg-[#eae7dcff] px-6 py-6 custom-scrollbar">
      {/* 24 Hours Window Alert - portaled with z-10 so it stays behind modals (z-[100]) */}
      {is24HoursWindowShow &&
        createPortal(
          <div className="fixed top-14 left-1/2 md:w-[30%] lg:w-[30%] xl:w-[40%] z-10 flex justify-between items-center shadow-md -translate-x-1/2">
            <Alert
              variant="warning"
              icon="FeatherClock"
              title="24-hour window closed"
              description="Personalized messages can only be sent within 24 hours of the customer's last reply. Use a template message to re-engage them now or schedule one for later."
            />
          </div>,
          document.body
        )}

      {/* Messages Container */}
      <div className="flex min-w-0 w-full max-w-full flex-col items-start gap-4">
        {groupedMessages ? (
          Object.entries(groupedMessages).map(([date, dayMessages]) => (
            <div key={date} className="w-full">
              {/* Date Separator */}
              <div className="flex items-center gap-2 my-4">
                <hr className="flex-grow border-t border-gray-300" />
                <span className="text-caption font-caption text-gray-500">
                  {moment(date).isSame(moment(), "day")
                    ? "Today"
                    : moment(date).format("D/MM/YYYY")}
                </span>
                <hr className="flex-grow border-t border-gray-300" />
              </div>

              {/* Messages for this date */}
              {dayMessages.map((message) => {
                // Sender side: system_user set, or system_user_payload with type audio (sender's voice message)
                const isSender =
                  message.system_user ||
                  (message.system_user_payload != null && message.system_user_payload?.type === 'audio');
                return (
                  <React.Fragment key={message.id}>
                    {isSender ? (
                      <SystemUser message={message} scrollRef={scrollRef} />
                    ) : (
                      <Recipient message={message} scrollRef={scrollRef} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center w-full">
            <Loader size="medium" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Message;
